import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Loader2, RefreshCw, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ReportSection } from './ReportSection';
import { ReportConfirmation } from './ReportConfirmation';
import { reportSections, reportMetrics, reportError, formatDate } from './reportConfig';
import './report.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/report`;
const notify = (message) => toast.success(<span data-testid="report-success-toast">{message}</span>, { id: 'report-success' });

export const ReportManifestManager = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const requestId = useRef(0);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/manifest`);
      if (id === requestId.current) { setReport(data); setError(''); }
    } catch (err) {
      if (id === requestId.current) setError(reportError(err));
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    window.addEventListener('focus', load);
    return () => { ++requestId.current; window.removeEventListener('focus', load); };
  }, [load]);

  const save = async (id, values) => {
    if (id) await axios.put(`${API}/manifest/${id}`, values);
    else await axios.post(`${API}/manifest`, values);
    notify(id ? 'Voce aggiornata' : 'Voce aggiunta');
    await load();
  };

  const confirm = async () => {
    setBusy(true);
    setActionError('');
    try {
      const endpoint = `${API}/manifest/${pending.entry.id}`;
      if (pending.action === 'delete') await axios.delete(endpoint);
      else await axios.post(`${endpoint}/${pending.action}`);
      notify(pending.action === 'completa' ? 'Attività completata' : pending.action === 'controllato' ? 'Controllo archiviato' : 'Voce eliminata');
      setPending(null);
      await load();
    } catch (err) { setActionError(reportError(err)); }
    finally { setBusy(false); }
  };

  const download = async () => {
    setDownloading(true);
    try {
      const { data } = await axios.get(`${API}/pdf`, { responseType: 'blob' });
      setError('');
      const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `ZonePass_Report_${report.oggi}.pdf`;
      link.dataset.testid = 'report-file-download-link';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setError('Impossibile scaricare il PDF. Riprova.');
    } finally { setDownloading(false); }
  };

  return (
    <div className="project-report space-y-8" data-testid="report-manager" aria-busy={loading}>
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
        <div>
          <p className="text-xs font-medium uppercase text-gray-500" data-testid="report-project-name">ZonePass / Progetto</p>
          <h2 className="mt-2 text-lg font-bold text-gray-900" data-testid="report-heading">Report di avanzamento</h2>
          {report && <p className="mt-1 text-xs text-gray-500" data-testid="report-as-of">Aggiornato al {formatDate(report.oggi)}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="icon" title="Aggiorna report" aria-label="Aggiorna report" data-testid="report-refresh" onClick={load} disabled={loading}><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></Button>
          <Button data-testid="report-download-pdf" onClick={download} disabled={!report || downloading || loading} className="gap-2 bg-blue-600 hover:bg-blue-700">{downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}{downloading ? 'Generazione PDF…' : 'Scarica Report PDF'}</Button>
        </div>
      </div>
      {error && <div role="alert" data-testid="report-load-error" className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800"><AlertCircle className="h-4 w-4 shrink-0" />{error}</div>}
      {!report && loading && <p role="status" data-testid="report-loading" className="py-8 text-sm text-gray-500">Caricamento del report…</p>}
      {report && <>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-7" data-testid="report-summary">
          {reportMetrics.map(([key, label, color]) => <div key={key} className="min-w-0 rounded-md border border-gray-200 bg-white px-3 py-4">
            <p data-testid={`report-count-${key.replaceAll('_', '-')}`} className="text-2xl font-semibold tabular-nums" style={{ color }}>{report.riepilogo[key]}</p>
            <p className="mt-2 text-xs leading-relaxed text-gray-500">{label}</p>
          </div>)}
        </div>
        {reportSections.map((config) => <ReportSection key={config.tipo} config={config} entries={report.voci[config.tipo]} today={report.oggi} onSave={save} onAction={(entry, action) => { setActionError(''); setPending({ entry, action }); }} />)}
      </>}
      <ReportConfirmation pending={pending} busy={busy} error={actionError} onClose={() => setPending(null)} onConfirm={confirm} />
    </div>
  );
};